import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { IconButton } from "rsuite";
import CompareIcon from "../../../../assets/images/icons/CompareIcon";
import DownloadIcon from "../../../../assets/images/icons/DownloadIcon";
import EyeOffIcon from "../../../../assets/images/icons/EyeOffIcon";
import { PlaygroundImage } from "../../../../models/playground";
import iframeStore from "../../../../pages/IframeNew/iframe-store.tsx";
import ImageOverlayFeedbackButtons from "../../../../pages/Playground/Images/Overlay/FeedbackButtons/ImageOverlayFeedbackButtons";
import ApiReferenceButton from "../../ApiReferenceButton/ApiReferenceButton";
import BriaButtonGroup from "../../BriaButtonGroup/BriaButtonGroup";
import BriaTooltip from "../../BriaTooltip/BriaTooltip";
import C2PAButton from "../../C2PAButton/C2PAButton.tsx";
import { FullScreenImageType } from "../Image/FullScreenImage";
import styles from "./FullScreenImageOverlay.module.scss";

type FullScreenImageOverlayProps = {
	image: FullScreenImageType;
	index: number;
	displayMode: "arrows" | "scroll";
	displayedImages: FullScreenImageType[];
	setDisplayedImages: (images: FullScreenImageType[]) => void;
	onDownload?: () => void;
	onCompare?: () => void;
	hideDownloadIcon?: boolean;
	displayHideIcon?: boolean;
	hideApiReferenceButton?: boolean;
	hideFeedbackButtons?: boolean;
};

const FullScreenImageOverlay = ({
	image,
	index,
	displayMode,
	displayedImages,
	setDisplayedImages,
	onDownload,
	onCompare,
	hideDownloadIcon = false,
	displayHideIcon = true,
	hideApiReferenceButton = true,
	hideFeedbackButtons = true,
}: FullScreenImageOverlayProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "briaImage.fullScreenView.overlay" });

	const isCampaign = (image as FullScreenImageType & PlaygroundImage).type === "campaign";
	const isUploaded = (image as FullScreenImageType & PlaygroundImage).type === "upload";
	const isFeedbackEnabled = iframeStore.iframe.config.general_config?.enable_feedback_buttons;
	const showFeedbackButtons =
		!hideFeedbackButtons && (!iframeStore.isIframe() || isFeedbackEnabled) && !isCampaign && !isUploaded;

	const compareImage = () => {
		onCompare && !displayedImages[index].openCompare && onCompare();
		setDisplayedImages(
			displayedImages.map((image, i) => (index === i ? { ...image, openCompare: !image.openCompare } : image)),
		);
	};

	const hideImage = () => {
		setDisplayedImages(displayedImages.map((image, i) => (index === i ? { ...image, hide: !image.hide } : image)));
	};

	const handleDownloadImage = () => {
		onDownload && onDownload();
	};

	return (
		<Box className={styles.fullScreenImageOverlayContainer}>
			<BriaButtonGroup className={styles.actionBtns} onClick={(e) => e.stopPropagation()}>
				<Box className={styles.rightSection}>
					{displayMode === "scroll" && image.compareTo && (
						<BriaTooltip title={t("compare")}>
							<IconButton
								className={clsx(styles.button, "iconHover", styles.compareBtn)}
								onClick={compareImage}
							>
								<CompareIcon />
							</IconButton>
						</BriaTooltip>
					)}
					{displayHideIcon &&
						displayMode === "scroll" &&
						displayedImages.filter((image) => !image.hide).length > 1 && (
							<BriaTooltip title={t("hide")}>
								<IconButton className={clsx(styles.button, "iconHover")} onClick={hideImage}>
									<EyeOffIcon />
								</IconButton>
							</BriaTooltip>
						)}
				</Box>

				<Box className={styles.leftSection}>
					{!hideDownloadIcon &&
						(!iframeStore.isIframe() || iframeStore.iframe.config.general_config?.enable_download) && (
							<BriaTooltip title={t("download")}>
								<IconButton
									className={clsx(styles.button, {
										iconHover: true,
									})}
									onClick={handleDownloadImage}
								>
									<Typography className={styles.imageFormat}>
										{(image.format ?? "png")?.toUpperCase()}
									</Typography>
									<DownloadIcon />
								</IconButton>
							</BriaTooltip>
						)}
					{!iframeStore.isIframe() && (
						<C2PAButton
							imageUrl={image.src}
							className={clsx({
								iconHover: true,
							})}
						/>
					)}
					{!hideApiReferenceButton && (
						<ApiReferenceButton
							image={image as FullScreenImageType & PlaygroundImage}
							className={styles.button}
						/>
					)}
				</Box>
			</BriaButtonGroup>
			<Box className={styles.imageFooter}>
				{showFeedbackButtons && (
					<ImageOverlayFeedbackButtons image={image as FullScreenImageType & PlaygroundImage} />
				)}
			</Box>
		</Box>
	);
};
export default observer(FullScreenImageOverlay);
